import {
  Card,
  CardBodyProps,
  CardHeaderProps,
  CardRootProps,
  FlexProps,
  Heading,
  useDisclosure,
} from '@chakra-ui/react';
import { ENVIRONMENT } from '@monetize/ui/config';
import useResizeObserver from '@react-hook/resize-observer';
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { FiDownload } from 'react-icons/fi';
import {
  MdChevronLeft,
  MdChevronRight,
  MdFullscreen,
  MdFullscreenExit,
} from 'react-icons/md';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MIcon,
  MPageLoader,
  MSkeleton,
  MText,
} from '..';
import { handleApiErrorToast } from '../../../api/axios';
import { PAPER_PAGE_MAX_WIDTH } from '../../../constants/quotes';
import { useGlobalWindowEventHandler } from '../../../hooks/useGlobalEventListener';
import { getSafeFilename } from '../../../utils';
import { downloadBlobAsFile } from '../../../utils/download';
import { Tooltip } from '../../ui/tooltip';

const WIDTH_BUFFER = 25;

pdfjs.GlobalWorkerOptions.workerSrc = ENVIRONMENT.PDFJS_WORKER_URL;

// TODO: improve the display of this
const LOADING_SKELETON_OFFERING_BUNDLE = () => {
  return (
    <>
      <MSkeleton height="20px" width="100%" mt="5" />
      <MBox pl="5">
        <MSkeleton height="12px" width="100%" mt="3" />
        <MSkeleton height="12px" width="100%" mt="1" />
        <MSkeleton height="12px" width="100%" mt="3" />
        <MSkeleton height="12px" width="100%" mt="1" />
      </MBox>
    </>
  );
};

const LOADING_SKELETON = (
  <MBox padding="10" w="100%">
    <MSkeleton height="25px" width="20%" />
    <MSkeleton height="12px" width="10%" mt="1" />
    <MSkeleton height="12px" width="15%" mt="1" />
    <MFlex alignItems="center" justifyContent="space-between" mt="10" w="100%">
      <MBox w="50%">
        <MSkeleton height="15px" width="30%" />
        <MSkeleton height="15px" width="35%" mt="1" />
        <MSkeleton height="15px" width="20%" mt="1" />
        <MSkeleton height="15px" width="25%" mt="1" />
      </MBox>
      <MBox w="50%" textAlign="right" display="flex" justifyContent="flex-end">
        <MSkeleton height="80px" width="50%" />
      </MBox>
    </MFlex>
    {/* offering header */}
    <MSkeleton height="20px" width="100%" mt="10" />
    <MSkeleton height="2px" width="100%" mt="1" />
    {/* Bundle header */}
    <LOADING_SKELETON_OFFERING_BUNDLE />
    <LOADING_SKELETON_OFFERING_BUNDLE />
    <LOADING_SKELETON_OFFERING_BUNDLE />
    <LOADING_SKELETON_OFFERING_BUNDLE />
    <LOADING_SKELETON_OFFERING_BUNDLE />
  </MBox>
);

interface PreviewPdfAccordionProps {
  downloadFileName: string;
  isLoading: boolean;
  pdfData?: ArrayBuffer | null;
  title?: string;
  maxWidth?: FlexProps['maxW'];
  cardProps?: {
    card?: CardRootProps;
    header?: CardHeaderProps;
    body?: CardBodyProps;
  };
}

export const PreviewPdfCard = ({
  downloadFileName,
  isLoading,
  pdfData,
  title = 'Agreement Preview',
  maxWidth = PAPER_PAGE_MAX_WIDTH,
  cardProps,
}: PreviewPdfAccordionProps) => {
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<DOMRect>();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  // react-pdf transfers the ArrayBuffer to the worker, so we pass it a clone so we can access the original
  const [clonedPdf, setClonedPdf] = useState<ArrayBuffer>();

  useGlobalWindowEventHandler('keydown', (e) => {
    if (e.key === 'Escape' && isFullScreenModalOpen) {
      onFullScreenModalClose();
    }
  });

  const {
    open: isFullScreenModalOpen,
    onClose: onFullScreenModalClose,
    onOpen: onFullScreenModalOpen,
  } = useDisclosure();

  useEffect(() => {
    if (pdfData) {
      setClonedPdf(pdfData.slice(0));
    }
  }, [pdfData]);

  // If browser is resized, the PDF will be adjusted to fit the new size
  useResizeObserver(pdfContainerRef, (entry) =>
    setSize({
      ...(entry.contentRect as DOMRect),
      width: entry.contentRect.width - WIDTH_BUFFER,
    }),
  );

  useLayoutEffect(() => {
    if (pdfContainerRef.current) {
      const roundingRect = pdfContainerRef.current.getBoundingClientRect();
      roundingRect &&
        setSize({
          ...roundingRect,
          width: roundingRect.width - WIDTH_BUFFER,
        });
    }
  }, [pdfContainerRef]);

  const onDocumentLoadSuccess = (pdf: PDFDocumentProxy) => {
    setNumPages(pdf?.numPages || 0);
  };

  const downloadPdf = async () => {
    try {
      if (pdfData) {
        downloadBlobAsFile(
          pdfData,
          `${getSafeFilename(`${downloadFileName}`)}.pdf`,
        );
      }
    } catch (err) {
      handleApiErrorToast(err);
    }
  };

  let _cardProps = {
    width: '100%',
    maxW: maxWidth,
    borderRadius: 'xl',
    // Clip the PDF/body content to the card's rounded corners so the bottom
    // border radius isn't painted over by the white body content.
    overflow: 'hidden',
    ...cardProps?.card,
  };
  if (isFullScreenModalOpen) {
    _cardProps = {
      ..._cardProps,
      position: 'fixed',
      top: '16px',
      left: '16px',
      right: '16px',
      bottom: '16px',
      zIndex: 'modal',
      borderRadius: 'xl',
      width: 'calc(100vw - 32px)',
      height: 'calc(100vh - 32px)',
      maxW: 'calc(100vw - 32px)',
    };
  }

  return (
    <Card.Root {..._cardProps}>
      <Card.Header
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        pb={1}
        {...cardProps?.header}
      >
        <Heading
          fontSize="lg"
          alignContent="center"
          display="flex"
          gap={2}
          w={['100%', 'auto']}
        >
          {title}
        </Heading>
        {numPages && (
          <MFlex alignItems="center" gap={2}>
            <MCustomIconButton
              boxSize={6}
              variant="icon"
              p={3}
              icon={MdChevronLeft}
              aria-label="Previous page"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
            />
            <MText fontSize="sm">
              {page} / {numPages}
            </MText>
            <MCustomIconButton
              boxSize={6}
              variant="icon"
              p={3}
              icon={MdChevronRight}
              aria-label="Next page"
              onClick={() =>
                setPage((prev) => Math.min(numPages as number, prev + 1))
              }
            />
          </MFlex>
        )}
        <MBox>
          <Tooltip
            content={isFullScreenModalOpen ? 'Exit full screen' : 'Full screen'}
            positioning={{
              placement: 'top',
            }}
          >
            <MCustomIconButton
              boxSize={8}
              btnSize={6}
              mr={1}
              onClick={() =>
                isFullScreenModalOpen
                  ? onFullScreenModalClose()
                  : onFullScreenModalOpen()
              }
              icon={isFullScreenModalOpen ? MdFullscreenExit : MdFullscreen}
              aria-label="Toggle full screen"
              color="tIndigo.base"
              iconColor="tIndigo.base"
            />
          </Tooltip>
          <MButton variant="tertiary" onClick={downloadPdf} size="sm">
            <MIcon as={FiDownload} mr={1} />
            Download
          </MButton>
        </MBox>
      </Card.Header>
      <Card.Body p={0} overflow="auto" {...cardProps?.body}>
        <MBox ref={pdfContainerRef} bg="white" justifySelf="center" w="100%">
          {isLoading && LOADING_SKELETON}
          {!isLoading && !!clonedPdf && (
            <MFlex w="full" flexDir="column">
              <Document
                loading={
                  <MFlex justify="center" grow={1}>
                    <MPageLoader />
                  </MFlex>
                }
                file={clonedPdf}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                <MFlex key={`page_${page}`} justify="center">
                  <Page
                    pageNumber={page}
                    className="pdf-page"
                    width={size?.width || PAPER_PAGE_MAX_WIDTH - WIDTH_BUFFER}
                  />
                </MFlex>
              </Document>
            </MFlex>
          )}
        </MBox>
      </Card.Body>
    </Card.Root>
  );
};
